import React from 'react';
import { AiDraftResult, AiRewriteResult } from '../api/assessmentsService';

const Modal: React.FC<{
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ title, subtitle, onClose, children, footer }) => (
  <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8">
    <div className="w-full max-w-4xl rounded-xl bg-white shadow-xl">
      <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-4 py-3 sm:px-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
        </div>
        <button
          type="button"
          className="text-sm text-gray-500 hover:text-gray-700"
          onClick={onClose}
        >
          Close
        </button>
      </div>
      <div className="px-4 py-4 sm:px-6">{children}</div>
      {footer && (
        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 px-4 py-3 sm:px-6">
          {footer}
        </div>
      )}
    </div>
  </div>
);

const AiDisclaimer: React.FC<{ model?: string }> = ({ model }) => (
  <p className="text-xs text-gray-500">
    Drafted from your notes only — candidate and spouse names were replaced before this text left the
    server. Nothing is saved until you insert it and save the report. Read every line before you use it.
    {model ? ` (${model})` : ''}
  </p>
);

const Spinner: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center gap-3 py-8 text-sm text-gray-600">
    <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-primary-600" />
    {label}
  </div>
);

export const AiDraftModal: React.FC<{
  isLoading: boolean;
  error: string | null;
  result: AiDraftResult | null;
  onClose: () => void;
  onUseCandidateFacing: (text: string, mode: 'append' | 'replace') => void;
  onUseInternal: (text: string, mode: 'append' | 'replace') => void;
  onRetry: () => void;
}> = ({ isLoading, error, result, onClose, onUseCandidateFacing, onUseInternal, onRetry }) => (
  <Modal
    title="Draft from my notes"
    subtitle="A starting point built from your interview notes. It never sets ratings or recommendations."
    onClose={onClose}
    footer={!isLoading && error ? (
      <button
        type="button"
        className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        onClick={onRetry}
      >
        Try again
      </button>
    ) : undefined}
  >
    {isLoading && <Spinner label="Reading your interview notes…" />}

    {!isLoading && error && (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
    )}

    {!isLoading && !error && result && (
      <div className="space-y-5">
        <AiDisclaimer model={result.model} />

        <section className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900">Report notes (shared with the candidate)</h3>
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="text-xs font-medium text-primary-600 hover:text-primary-700"
                onClick={() => onUseCandidateFacing(result.candidateFacing, 'append')}
              >
                Insert below
              </button>
              <button
                type="button"
                className="text-xs font-medium text-primary-600 hover:text-primary-700"
                onClick={() => onUseCandidateFacing(result.candidateFacing, 'replace')}
              >
                Replace
              </button>
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 whitespace-pre-wrap">
            {result.candidateFacing || 'The model returned nothing for this section.'}
          </div>
        </section>

        <section className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900">After-part notes (team only)</h3>
            {result.internalNotes && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="text-xs font-medium text-primary-600 hover:text-primary-700"
                  onClick={() => onUseInternal(result.internalNotes, 'append')}
                >
                  Insert below
                </button>
                <button
                  type="button"
                  className="text-xs font-medium text-primary-600 hover:text-primary-700"
                  onClick={() => onUseInternal(result.internalNotes, 'replace')}
                >
                  Replace
                </button>
              </div>
            )}
          </div>
          <div className="max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 whitespace-pre-wrap">
            {result.internalNotes || 'Nothing in your notes looked team-only.'}
          </div>
        </section>
      </div>
    )}
  </Modal>
);

export const AiRewriteModal: React.FC<{
  isLoading: boolean;
  error: string | null;
  result: AiRewriteResult | null;
  original: string;
  onClose: () => void;
  onAccept: (text: string) => void;
  onRetry: () => void;
}> = ({ isLoading, error, result, original, onClose, onAccept, onRetry }) => (
  <Modal
    title="Make candidate-ready"
    subtitle="Same facts and same concerns, reworded for the person who will read it."
    onClose={onClose}
    footer={(
      <>
        {!isLoading && error && (
          <button
            type="button"
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            onClick={onRetry}
          >
            Try again
          </button>
        )}
        {!isLoading && !error && result && (
          <>
            <button
              type="button"
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={onClose}
            >
              Keep mine
            </button>
            <button
              type="button"
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
              onClick={() => onAccept(result.rewritten)}
            >
              Use the rewrite
            </button>
          </>
        )}
      </>
    )}
  >
    {isLoading && <Spinner label="Rewriting your draft…" />}

    {!isLoading && error && (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
    )}

    {!isLoading && !error && result && (
      <div className="space-y-4">
        <AiDisclaimer model={result.model} />

        <div className="grid gap-4 lg:grid-cols-2">
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Yours</div>
            <div className="max-h-80 overflow-y-auto rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-800 whitespace-pre-wrap">
              {original}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-primary-700">Rewritten</div>
            <div className="max-h-80 overflow-y-auto rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-gray-900 whitespace-pre-wrap">
              {result.rewritten}
            </div>
          </div>
        </div>

        {result.changes.length > 0 && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">What changed</div>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-gray-700">
              {result.changes.map((change, index) => (
                <li key={index}>{change}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )}
  </Modal>
);
